import { Component, EventEmitter, Input, Output } from '@angular/core';
import icPlus from '@iconify/icons-ic/twotone-plus';
import icMinus from '@iconify/icons-ic/twotone-minus';

/**
 * Évènement émis par le quantity-stepper.
 * - `quantity` : nouvelle quantité déjà bornée à [min, max]
 * - `direction` : `plus`/`minus` (boutons) ou `change` (saisie directe)
 */
export interface QuantityChange {
  quantity: number;
  direction: 'minus' | 'plus' | 'change';
}

/**
 * Composant de pas de quantité réutilisable (boutons − / + + input).
 *
 * Présentationnel : il ne connaît rien du métier (item, panier, persistance).
 * Il borne la valeur à [step, max] (clamping, jamais de suppression) et émet
 * la nouvelle quantité. Le `step` paramètre l'incrément (1 par défaut, 0.25
 * pour les tickets COMPETENCE).
 */
@Component({
  selector: 'quantity-stepper',
  templateUrl: './quantity-stepper.component.html',
})
export class QuantityStepperComponent {
  // Valeur de travail interne : mise à jour de façon optimiste à chaque clic pour
  // que les clics rapides s'accumulent sans attendre le cycle de change detection.
  private _quantity = 0;

  @Input()
  set quantity(value: number) {
    this._quantity = value ?? 0;
  }
  get quantity(): number {
    return this._quantity;
  }

  @Input() step = 1;
  @Input() max = 9999;
  @Input() disabled = false;

  /** Texte optionnel affiché à droite du stepper (ex: équivalent en jours "0,25 j") */
  @Input() hint: string | null = null;

  @Output() quantityChange = new EventEmitter<QuantityChange>();

  icPlus = icPlus;
  icMinus = icMinus;

  /** Quantité minimale = un pas (on borne, on ne descend jamais en dessous) */
  get min(): number {
    return this.step;
  }

  decrement(): void {
    if (this.disabled) return;
    // Descendre au multiple de pas strictement inférieur (recale les valeurs hors grille)
    const next = this.normalize((Math.ceil(this._quantity / this.step) - 1) * this.step);
    // Déjà au minimum : on borne (la suppression passe par le bouton dédié)
    if (next < this.min) return;
    this._quantity = next;
    this.emit(next, 'minus');
  }

  increment(): void {
    if (this.disabled) return;
    // Monter au multiple de pas strictement supérieur (recale les valeurs hors grille)
    const next = Math.min(this.normalize((Math.floor(this._quantity / this.step) + 1) * this.step), this.max);
    this._quantity = next;
    this.emit(next, 'plus');
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(',', '.'); // Permettre la saisie avec virgule (ex: 0,25)
    const raw = parseFloat(input.value);
    let next: number;
    if (isNaN(raw)) {
      next = this.min;
    } else {
      // Recaler la saisie sur la grille de pas (ex: 0.4 -> 0.5 pour un pas de 0.25)
      next = this.snapToStep(raw);
      if (next < this.min) next = this.min;
      else if (next > this.max) next = this.max;
    }
    this._quantity = next;
    input.value = String(next);
    this.emit(next, 'change');
  }

  private emit(quantity: number, direction: 'minus' | 'plus' | 'change'): void {
    this.quantityChange.emit({ quantity, direction });
  }

  /** Arrondit au multiple de pas le plus proche */
  private snapToStep(value: number): number {
    return this.normalize(Math.round(value / this.step) * this.step);
  }

  /** Évite la dérive flottante en arrondissant à 4 décimales */
  private normalize(value: number): number {
    return Math.round(value * 10000) / 10000;
  }
}
